import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { t } from '../content';
import { canhBao, Glyph } from '../kit/glyphs-core';
import { useUi } from './context';
import { LazySheet, preloadSheets } from './lazy-sheet';
import { Toast } from './toast';

export function App(): JSX.Element {
  const { store, bus } = useUi();
  const load = store.loadState.value;
  const phase = store.phase.value;
  const sheetOpen = store.sheet.value !== 'none' || store.activeActivity.value !== null;

  if (phase === 'intro') preloadSheets();

  if (load === 'no-webgl' || load === 'error') {
    const prefix = load === 'no-webgl' ? 'error.noWebgl' : 'error.load';
    const tips = t(`${prefix}.tips`).split('|');
    return (
      <div class="err-page" role="alert">
        <div class="err-card">
          <Glyph of={canhBao} size={32} />
          <h2>{t(`${prefix}.title`)}</h2>
          <p>{t(`${prefix}.body`)}</p>
          <ul class="muted">
            {tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
          <button class="btn btn-primary wide" onClick={() => location.reload()}>
            {t(`${prefix}.reload`)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {phase === 'intro' && <IntroSkip onSkip={() => bus.emit('ui:skip-intro')} />}
      {phase === 'play' &&
        store.activeActivity.value === null &&
        store.approaching.value === null &&
        !sheetOpen && <LazySheet name="hud" />}
      {phase === 'play' && store.tutorialStep.value > 0 && !sheetOpen && (
        <LazySheet name="tutorial" />
      )}
      {store.approaching.value && <LazySheet name="walk" />}
      <div class="safe">
        <Toast />
      </div>
      {phase === 'name' && <LazySheet name="name" />}
      {store.readPrayer.value && <LazySheet name="prayer" />}
      {store.sheet.value !== 'none' && <LazySheet name={store.sheet.value} />}
    </>
  );
}

function IntroSkip({ onSkip }: { onSkip: () => void }): JSX.Element {
  useEffect(() => {
    const skip = (): void => onSkip();
    window.addEventListener('pointerdown', skip);
    return () => window.removeEventListener('pointerdown', skip);
  }, [onSkip]);
  return (
    <div class="safe">
      <div class="skip-pill">{t('splash.skipHint')}</div>
    </div>
  );
}
